import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import * as Yup from "yup";

/* No TWStylesWrapper here: its global.css would double the bundle —
   lib/index.css builds the same theme/components/fonts widget-scoped. */
import {
  LoanWidget,
  LoanWidgetConfigSchema,
} from "@krea/shared/features/loan-widget";
import "./index.css";

import { ReviewProviderBadgeList } from "@krea/customer-features/shared-components/app/reviews/CustomerReviewProviderBadge";
import { useCustomerFeaturesSettingsStore } from "@krea/customer-features/store/app-settings/useCustomerFeaturesSettingsStore";
import { useLoanApplicationFormStore } from "@krea/customer-features/store/loan-application-form/hooks";
import { getPrimaryLanguage } from "@krea/customer-features/utils/country";
import { CountryCode } from "@krea/shared/types/common";

import i18n from "./i18n";
import settings from "./settings";
import { hexToRgb, lightenHexToRgb } from "./utils";

const WidgetPropsSchema = Yup.object({
  utmSourceIdentifier: Yup.string()
    .strict()
    .defined("UTM Source Identifier is required; obtain this from Krea"),
  utmCampaign: Yup.string().optional(),
  affiliatePartnerId: Yup.string().optional(),
  countryCode: Yup.mixed<CountryCode>()
    .oneOf([CountryCode.SE, CountryCode.FI])
    .required(
      `Country code is required, supported values: ${CountryCode.SE}, ${CountryCode.FI}`,
    ),
  language: Yup.string<"sv" | "fi" | "en">()
    .oneOf(["sv", "fi", "en"])
    .required(`Language is required, supported values: sv, fi, en`)
    .test(
      "matches-country-code",
      "Language must match countryCode: SE supports ['sv', 'en'], FI supports ['fi', 'sv']",
      function (value) {
        const countryCode = this.parent.countryCode as CountryCode | undefined;
        if (!countryCode || !value) return true;
        let supportedLanguages: string[] = [];
        switch (countryCode) {
          case CountryCode.SE:
            supportedLanguages = ["sv", "en"];
            break;
          case CountryCode.FI:
            supportedLanguages = ["fi", "sv"];
            break;
          default: {
            const exhaustiveCheck: never = countryCode;
            console.error(`Exhaustive check failed: ${exhaustiveCheck}`);
          }
        }
        return supportedLanguages.includes(value);
      },
    ),
  uiSettings: Yup.object({
    primaryColor: Yup.string().optional(),
    primaryColorLight: Yup.string().optional(),
    accentColor: Yup.string().optional(),
    accentColorLight: Yup.string().optional(),
    ctaColor: Yup.string().optional(),
  }).optional(),
  amountEstimatorWidget: LoanWidgetConfigSchema,
});

export type WidgetProps = Yup.InferType<typeof WidgetPropsSchema>;

const HEX_COLOR_PATTERN = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const isValidHex = (label: string, hex: string | undefined): hex is string => {
  if (!hex) return false;
  if (!HEX_COLOR_PATTERN.test(hex)) {
    console.error(
      `Ignoring uiSettings.${label}: "${hex}" is not a #RGB/#RRGGBB hex color`,
    );
    return false;
  }
  return true;
};

/* hexToRgb/lightenHexToRgb throw on malformed input — validate each color so
   a bad embed config degrades to default colors instead of crashing render. */
const applyThemeColorOverrides = (
  container: HTMLDivElement,
  uiSettings: WidgetProps["uiSettings"],
) => {
  const {
    primaryColor,
    primaryColorLight,
    accentColor,
    accentColorLight,
    ctaColor,
  } = uiSettings ?? {};

  if (isValidHex("primaryColor", primaryColor)) {
    container.style.setProperty(
      "--ka-widget-color-primary",
      hexToRgb(primaryColor),
    );
    if (!primaryColorLight) {
      container.style.setProperty(
        "--ka-widget-color-primary-light",
        lightenHexToRgb(primaryColor, 50),
      );
    }
  }

  if (isValidHex("primaryColorLight", primaryColorLight)) {
    container.style.setProperty(
      "--ka-widget-color-primary-light",
      hexToRgb(primaryColorLight),
    );
  }

  if (isValidHex("accentColor", accentColor)) {
    container.style.setProperty(
      "--ka-widget-color-accent",
      hexToRgb(accentColor),
    );
    if (!accentColorLight) {
      container.style.setProperty(
        "--ka-widget-color-accent-light",
        lightenHexToRgb(accentColor, 50),
      );
    }
  }

  if (isValidHex("accentColorLight", accentColorLight)) {
    container.style.setProperty(
      "--ka-widget-color-accent-light",
      hexToRgb(accentColorLight),
    );
  }

  if (isValidHex("ctaColor", ctaColor)) {
    container.style.setProperty("--ka-widget-color-cta", hexToRgb(ctaColor));
  }
};

const WidgetContent = (
  props: WidgetProps & { portalContainer?: HTMLDivElement | null },
) => {
  const { portalContainer } = props;
  const { countryCode, currency } = useCustomerFeaturesSettingsStore(
    (state) => ({
      countryCode: state.countryCode,
      currency: state.currency,
    }),
  );

  const {
    organisation,
    email,
    mobilePhoneNumber,
    requestedLoanAmount,
    consentCheckbox,
    preliminaryBid,
    setOrganisation,
    setEmail,
    setMobilePhoneNumber,
    setRequestedLoanAmount,
    setConsentCheckbox,
    setPreliminaryBid,
    removeFormError,
  } = useLoanApplicationFormStore((state) => ({
    organisation: state.organisation,
    email: state.email,
    mobilePhoneNumber: state.mobilePhoneNumber,
    requestedLoanAmount: state.requestedLoanAmount,
    consentCheckbox: state.consentCheckbox,
    preliminaryBid: state.preliminaryBid,
    setOrganisation: state.setOrganisation,
    setEmail: state.setEmail,
    setMobilePhoneNumber: state.setMobilePhoneNumber,
    setRequestedLoanAmount: state.setRequestedLoanAmount,
    setConsentCheckbox: state.setConsentCheckbox,
    setPreliminaryBid: state.setPreliminaryBid,
    removeFormError: state.removeFormError,
  }));

  return (
    <LoanWidget
      {...props.amountEstimatorWidget}
      countryCode={countryCode}
      currency={currency}
      locale={getPrimaryLanguage()}
      backendBaseUrl={settings.backendBaseUrl ?? ""}
      customerWebUrl={settings.customerWebUrl ?? ""}
      countryWebsiteUrl={settings.countryWebsiteUrl}
      environment={settings.environment}
      utmSourceIdentifier={props.utmSourceIdentifier}
      utmCampaign={props.utmCampaign}
      affiliatePartnerId={props.affiliatePartnerId}
      defaultOrganisation={organisation}
      defaultEmail={email}
      defaultMobilePhoneNumber={mobilePhoneNumber}
      defaultRequestedLoanAmount={requestedLoanAmount}
      defaultConsentCheckbox={consentCheckbox}
      initialPreliminaryBid={preliminaryBid}
      onOrganisationChange={setOrganisation}
      onEmailChange={setEmail}
      onMobilePhoneNumberChange={setMobilePhoneNumber}
      onRequestedLoanAmountChange={setRequestedLoanAmount}
      onConsentChange={setConsentCheckbox}
      onClearFormError={removeFormError}
      onClearPreliminaryBid={() => setPreliminaryBid(undefined)}
      onSubmitSuccess={setPreliminaryBid}
      renderReviewBadges={(cc) => (
        <ReviewProviderBadgeList countryCode={cc} isSimpleVersion />
      )}
      portalContainer={portalContainer}
    />
  );
};

export const Widget = (props: WidgetProps) => {
  const [widgetContainer, setWidgetContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: !import.meta.env.DEV,
          },
        },
      }),
  );

  useEffect(() => {
    if (!("amountEstimatorWidget" in props)) {
      throw new Error(
        "amountEstimatorWidget is required. It can be an empty object, but its required",
      );
    }
    WidgetPropsSchema.validateSync(props, {
      disableStackTrace: true,
    });

    // The --ka-widget-* defaults live on .ka3r (prefixer re-targets :root there),
    // so overrides must be inline on that element — <html> would lose to it.
    if (!widgetContainer) {
      return;
    }

    applyThemeColorOverrides(widgetContainer, props?.uiSettings);
  }, [props, widgetContainer]);

  return (
    <React.StrictMode>
      <I18nextProvider i18n={i18n}>
        {/*
          Important className (ka3r)!!
          This is scoping styles, so all tailwind classes are scoped under this className
        */}
        <div className="ka3r" ref={setWidgetContainer}>
          <QueryClientProvider client={queryClient}>
            <WidgetContent {...props} portalContainer={widgetContainer} />
          </QueryClientProvider>
        </div>
      </I18nextProvider>
    </React.StrictMode>
  );
};
